import { createFileRoute, Link } from "@tanstack/react-router";
import { useScope } from "@/hooks/use-scope";
import {
  useImplementationProgress,
  TRACK_META,
  type TrackId,
  type TrackProgress,
} from "@/lib/implementation-progress";
import { StatusDot } from "./implementation.workspace";
import { ArrowRight } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/implementation/workspace/")({
  component: WorkspaceOverview,
});

function WorkspaceOverview() {
  const { tenantId } = useScope();
  const progress = useImplementationProgress(tenantId);
  const order: TrackId[] = ["foundation", "data", "operations", "validation"];
  const currentTrack = progress.tracks[progress.currentTrack];
  const currentMeta = TRACK_META[progress.currentTrack];

  return (
    <div className="space-y-8">
      {/* "Where you are" banner */}
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
              You are here
            </div>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">
              {currentMeta.name}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              {currentMeta.promise}
            </p>
            <p className="mt-3 text-sm text-foreground">
              <span className="font-medium">Next:</span>{" "}
              <span className="text-muted-foreground">
                {currentTrack.status === "complete"
                  ? "This track is complete. Move on to the next one."
                  : currentMeta.next}
              </span>
            </p>
          </div>
          <Link
            to={currentMeta.path}
            className="inline-flex h-10 items-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
          >
            Continue {currentMeta.name}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Four track cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        {order.map((id, i) => (
          <TrackCard
            key={id}
            index={String(i + 1).padStart(2, "0")}
            track={progress.tracks[id]}
            isCurrent={progress.currentTrack === id}
          />
        ))}
      </div>

      {/* Footer principle */}
      <div className="rounded-lg border border-dashed border-border bg-muted/30 p-5">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          A word on this workspace
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          The Implementation Workspace doesn't disappear at go-live. It becomes your operational
          readiness surface — where you add facilities, enable new capabilities, onboard new
          merchants, and prepare future changes. Its role changes; its home does not.
        </p>
      </div>
    </div>
  );
}

function TrackCard({
  index,
  track,
  isCurrent,
}: {
  index: string;
  track: TrackProgress;
  isCurrent: boolean;
}) {
  const meta = TRACK_META[track.id];
  return (
    <Link
      to={meta.path}
      className={
        "group relative flex flex-col rounded-xl border bg-card p-6 transition-all " +
        (isCurrent
          ? "border-primary/40 shadow-[var(--shadow-card)]"
          : "border-border hover:border-primary/30 hover:shadow-[var(--shadow-card)]")
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="font-mono text-xs text-muted-foreground/70">{index}</div>
          <div className="text-base font-semibold text-foreground">{meta.name}</div>
          <StatusPill status={track.status} />
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
      </div>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{meta.description}</p>

      <div className="mt-5 space-y-2">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
          <span>Progress</span>
          <span className="tabular-nums text-foreground">
            {track.completed} / {track.total}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={
              "h-full transition-[width] duration-500 " +
              (track.status === "complete" ? "bg-[color:var(--success)]" : "bg-primary")
            }
            style={{ width: `${track.percent}%` }}
          />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-1.5 border-t border-border/60 pt-4">
        {track.items.slice(0, 4).map((item) => (
          <div key={item.id} className="flex items-center gap-2 text-xs">
            <StatusDot status={item.done ? "complete" : "not_started"} />
            <span className={item.done ? "text-foreground" : "text-muted-foreground"}>
              {item.label}
            </span>
            {item.hint ? (
              <span className="ml-auto font-mono text-[10px] text-muted-foreground/70">
                {item.hint}
              </span>
            ) : null}
          </div>
        ))}
        {track.items.length > 4 ? (
          <div className="mt-1 text-[11px] text-muted-foreground/70">
            + {track.items.length - 4} more
          </div>
        ) : null}
      </div>
    </Link>
  );
}

function StatusPill({ status }: { status: TrackProgress["status"] }) {
  if (status === "complete") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--success)]/20 bg-[color:var(--success)]/10 px-2 py-0.5 text-[10px] font-medium text-[color:var(--success)]">
        Complete
      </span>
    );
  }
  if (status === "in_progress") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
        In progress
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
      Not started
    </span>
  );
}